<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search medias..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      :active-filter-count="activeFilterCount"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchMedias"
      @page-change="goToPage"
      @clear-filters="clearFilters"
    >
      <template #filters>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Publish Status</label>
          <select v-model="filters.publish_status">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">File Status</label>
          <select v-model="filters.file_status">
            <option value="">All</option>
            <option value="uploading">Uploading</option>
            <option value="transcoding">Transcoding</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Tag</label>
          <TagSelect v-model="filters.tags" placeholder="Filter by tags" :allow-create="false" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Category</label>
          <CategorySelect ref="categorySelect" v-model="filters.category_id" placeholder="All categories" preload />
        </div>
      </template>
      <template v-if="$checkPermission('medias.create')" #actions>
        <button
          @click="showUpload = true"
          class="btn-primary"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          Upload
        </button>
      </template>
    </ListToolbar>

    <!-- Upload progress -->
    <div v-if="uploads.length" class="mb-6 space-y-2">
      <div
        v-for="upload in uploads"
        :key="upload.name"
        class="flex items-center gap-4 bg-white rounded-xl border border-gray-200/60 px-5 py-3"
      >
        <div class="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
          <svg class="w-4 h-4 text-[var(--color-primary)] animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 truncate">{{ upload.name }}</p>
          <div class="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              class="h-full bg-[var(--color-primary)] rounded-full transition-all duration-300"
              :style="{ width: upload.progress + '%' }"
            />
          </div>
        </div>
        <span class="text-sm font-data text-gray-500 shrink-0">{{ upload.progress }}%</span>
      </div>
    </div>

    <!-- Table -->
    <div v-if="loading || medias.length" class="bg-white rounded-xl border border-gray-200/60 table-scroll card-shadow" :class="{ 'animate-fade-in': !loading }">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Name</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Duration</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Size</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">File Status</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden sm:table-cell">Tags</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Category</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Created</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Actions</th>
          </tr>
        </thead>
        <!-- Skeleton rows -->
        <tbody v-if="loading">
          <tr v-for="i in 8" :key="'skel-' + i" class="border-b border-gray-50">
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-gray-100 skeleton-pulse shrink-0" />
                <div class="space-y-2 flex-1">
                  <div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" />
                  <div class="h-3 bg-gray-50 rounded skeleton-pulse w-16" />
                </div>
              </div>
            </td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-16" /></td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-14" /></td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-16" /></td>
            <td class="px-6 py-4 hidden sm:table-cell"><div class="h-6 bg-gray-100 rounded-md skeleton-pulse w-10" /></td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-6 bg-gray-100 rounded-md skeleton-pulse w-10" /></td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-24" /></td>
            <td class="px-6 py-4 text-right"><div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-8 ml-auto" /></td>
          </tr>
        </tbody>
        <!-- Real rows -->
        <tbody v-else class="divide-y divide-gray-50">
          <tr
            v-for="media in medias"
            :key="media._id"
            class="hover:bg-gray-50/50 transition-colors"
          >
            <!-- Name + publish status -->
            <td class="px-6 py-4 max-w-[300px]">
              <div class="flex items-center gap-3">
                <img v-if="media.thumb_url" :src="$mediaUrl.thumbUrl(media.thumb_url)" :alt="media.title" :class="['rounded-lg object-cover shrink-0 bg-gray-100', isVerticalThumb(media) ? 'w-8 h-12' : 'w-16 h-10']" />
                <div v-else class="w-16 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                  <svg class="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                  </svg>
                </div>
                <div class="min-w-0">
                  <router-link v-if="!isProcessing(media) && $checkPermission('medias.update')" :to="{ name: 'medias-edit', params: { id: media._id } }" class="text-[14px] text-gray-900 hover:text-[var(--color-primary)] transition-colors truncate block">{{ media.title || media.name || media.filename }}</router-link>
                  <span v-else class="text-[14px] text-gray-900 truncate block">{{ media.title || media.name || media.filename }}</span>
                  <div class="flex items-center gap-1.5 mt-1">
                    <span :class="['w-1.5 h-1.5 rounded-full', media.status === 'published' ? 'bg-green-500' : 'bg-gray-300']" />
                    <span class="text-sm text-gray-500 capitalize">{{ media.status || 'draft' }}</span>
                  </div>
                </div>
              </div>
            </td>

            <!-- Duration -->
            <td class="px-6 py-4 hidden md:table-cell">
              <span class="text-sm font-data text-gray-500">{{ formatDuration(media.file_object?.duration || media.duration) }}</span>
            </td>
            <!-- Size -->
            <td class="px-6 py-4 hidden md:table-cell">
              <span class="text-sm font-data text-gray-500">{{ formatSize(media.file_object?.size || media.size || 0) }}</span>
            </td>

            <!-- File Status -->
            <td class="px-6 py-4 hidden md:table-cell">
              <span :class="fileStatusClass(media)">
                {{ media.file_object?.status || '—' }}
              </span>
              <div v-if="isProcessing(media) && media.file_object?.progress > 0" class="flex items-center gap-2 mt-1.5">
                <div class="h-1.5 w-20 bg-gray-100 rounded-full overflow-hidden">
                  <div class="h-full bg-[var(--color-primary)] rounded-full transition-all duration-300" :style="{ width: (media.file_object.progress || 0) + '%' }" />
                </div>
                <span class="text-sm font-data text-gray-500">{{ media.file_object.progress }}%</span>
              </div>
            </td>

            <!-- Tags -->
            <td class="px-6 py-4 hidden sm:table-cell">
              <button
                v-if="media.tags && media.tags.length"
                @click="openQuickTags(media)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium bg-[var(--color-primary-light)] text-[var(--color-primary)] hover:bg-[var(--color-primary-glow)] transition-colors"
              >
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                {{ media.tags.length }}
              </button>
              <button v-else @click="openQuickTags(media)" class="text-sm text-gray-400 hover:text-gray-500 transition-colors">+ Add</button>
            </td>

            <!-- Category -->
            <td class="px-6 py-4 hidden md:table-cell">
              <button
                v-if="media.categories && media.categories.length"
                @click="openQuickCategory(media)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium bg-violet-50 text-violet-500 hover:bg-violet-100 transition-colors"
              >
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                {{ media.categories.length }}
              </button>
              <button
                v-else
                @click="openQuickCategory(media)"
                class="text-sm text-gray-400 hover:text-gray-500 transition-colors"
              >
                + Add
              </button>
            </td>


            <!-- Created -->
            <td class="px-6 py-4 hidden lg:table-cell">
              <span class="text-sm text-gray-500">{{ $filters.formatDatetime(media.created_at) }}</span>
            </td>

            <!-- Actions -->
            <td class="px-6 py-4 text-right">
              <div v-if="!isProcessing(media)" class="flex items-center justify-end gap-1">
                <YouTubePublishButton
                  v-if="media.file_object?.status === 'success' && getPublishableFormats(media).length"
                  :media-id="media._id"
                  :media-title="media.title || ''"
                  :media-description="media.description || ''"
                  :formats="getPublishableFormats(media)"
                  :youtube-publishes="media.youtubePublishes || {}"
                  @updated="media.youtubePublishes = $event"
                />
                <ActionsMenu :items="mediaActions(media)" />
              </div>
              <div v-else class="flex items-center justify-end">
                <svg class="w-4 h-4 text-gray-400 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83"/></svg>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="search || filters.publish_status || filters.file_status || filters.tags?.length || filters.category_id" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center animate-fade-in card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching medias found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
    </div>

    <!-- Empty: no records -->
    <div v-else class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center animate-fade-in card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
        </svg>
      </div>
      <p class="text-base font-medium text-gray-900">No medias yet</p>
      <p class="text-sm text-gray-500 mt-1 mb-5">Upload your first media file to get started</p>
      <button
        @click="showUpload = true"
        class="btn-primary"
      >
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        Upload Media
      </button>
    </div>

    <!-- Delete confirm -->
    <ConfirmModal
      :show="showDeleteModal" :loading="deleting"
      title="Delete Media"
      :message="`Are you sure you want to delete '${mediaToDelete?.title || mediaToDelete?.name || mediaToDelete?.filename}'? This action cannot be undone.`"
      confirm-text="Delete"
      :danger="true"
      @confirm="deleteMedia"
      @cancel="showDeleteModal = false"
    />

    <!-- Upload modal -->
    <UploadMedia v-if="showUpload" @closed="showUpload = false" @created="fetchMedias" />

    <!-- Quick edit tags -->
    <QuickEditModal :show="showQuickTags" title="Edit Tags" :saving="quickSaving" @close="showQuickTags = false" @save="saveQuickTags">
      <TagSelect v-model="quickTagIds" />
    </QuickEditModal>

    <!-- Hidden lookups (always mounted for name resolution) -->
    <CategorySelect ref="categoryLookup" class="hidden" preload />
    <TagSelect ref="tagLookup" class="hidden" preload />

    <!-- Quick edit category -->
    <QuickEditModal :show="showQuickCategory" title="Edit Categories" :saving="quickSaving" @close="showQuickCategory = false" @save="saveQuickCategory">
      <CategorySelect v-model="quickCategoryIds" />
    </QuickEditModal>

    <!-- Player modal -->
    <MediaPlayerModal
      :show="showPlayer"
      :title="previewMedia?.title || previewMedia?.name || 'Preview'"
      :hls-url="previewHlsUrl"
      :formats="previewFormats"
      @close="showPlayer = false; previewMedia = null"
    />
  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import icons from '../../components/action-icons.js'
import ConfirmModal from '../../components/ConfirmModal.vue'
import MediaPlayerModal from '../../components/MediaPlayerModal.vue'
import QuickEditModal from '../../components/QuickEditModal.vue'
import TagSelect from '../../components/TagSelect.vue'
import CategorySelect from '../../components/CategorySelect.vue'
import UploadMedia from './upload.vue'
import YouTubePublishButton from '../../components/YouTubePublishButton.vue'
import api from '../../services/api.js'
import { notifySuccess } from '../../services/notifications.js'
import websocket from '../../services/websocket.js'

export default {
  name: 'MediasView',
  components: { ListToolbar, ActionsMenu, ConfirmModal, MediaPlayerModal, QuickEditModal, TagSelect, CategorySelect, UploadMedia, YouTubePublishButton },

  data() {
    return {
      medias: [],
      search: '',
      filters: {
        publish_status: '',
        file_status: '',
        tags: [],
        category_id: null
      },
      sort: 'created:-1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' },
        { value: 'name:-1', label: 'Name Z-A' },
        { value: 'status:1', label: 'Status A-Z' },
        { value: 'status:-1', label: 'Status Z-A' },
        { value: 'file_status:1', label: 'File Status A-Z' },
        { value: 'file_status:-1', label: 'File Status Z-A' },
        { value: 'filesize:1', label: 'File Size (smallest)' },
        { value: 'filesize:-1', label: 'File Size (largest)' },
        { value: 'updated:1', label: 'Last Updated (oldest)' },
        { value: 'updated:-1', label: 'Last Updated (newest)' },
        { value: 'created:1', label: 'Created (oldest)' },
        { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 50, page: 1, total: 0 },
      loading: true,
      uploads: [],
      showUpload: false,
      showPlayer: false,
      previewMedia: null,
      showDeleteModal: false, deleting: false,
      showQuickTags: false,
      showQuickCategory: false,
      quickSaving: false,
      quickEditMedia: null,
      quickTagIds: [],
      quickCategoryIds: [],
      mediaToDelete: null,
      expandedTagsId: null
    }
  },

  computed: {
    activeFilterCount() {
      return Object.values(this.filters).filter(v => Array.isArray(v) ? v.length > 0 : v !== '' && v != null).length
    },
    previewFormats() {
      if (!this.previewMedia?.exported_files) return []
      return Object.entries(this.previewMedia.exported_files)
        .filter(([, file]) => file.remoteUrl || file.remotePath)
        .map(([id, file]) => ({
          id,
          remoteUrl: this.$mediaUrl.media(this.previewMedia._id, id),
          fileSize: file.fileSize,
          status: file.status || 'success'
        }))
    },
    previewHlsUrl() {
      if (!this.previewMedia?._id) return ''
      // Only show HLS row if media has original_abr or url.hls
      const hasAbr = this.previewMedia?.exported_files?.original_abr?.remoteUrl || this.previewMedia?.exported_files?.original_abr?.remotePath
      const hasHls = this.previewMedia?.url?.hls
      if (!hasAbr && !hasHls) return ''
      return this.$mediaUrl.media(this.previewMedia._id, 'hls')
    }
  },

  watch: {
    search() { this.pagination.page = 1; this.fetchMedias() },
    sort() { this.pagination.page = 1; this.fetchMedias() },
    filters: { deep: true, handler() { this.pagination.page = 1; this.fetchMedias() } }
  },

  async mounted() {
    await this.fetchMedias()
    websocket.init()
    websocket.on('media:update', this.handleMediaUpdate)
  },

  beforeUnmount() {
    websocket.off('media:update', this.handleMediaUpdate)
  },

  methods: {
    isVerticalThumb(media) {
      return media.file_object?.orientation === 'vertical'
    },
    handleMediaUpdate(data) {
      const id = data._id?.toString()
      if (!id) return
      const idx = this.medias.findIndex(item => item._id?.toString() === id)
      if (idx < 0) return

      const item = this.medias[idx]
      this.medias.splice(idx, 1, {
        ...item,
        file_object: {
          ...item.file_object,
          status: data.file_object?.status || item.file_object?.status,
          progress: data.file_object?.progress ?? item.file_object?.progress,
          size: data.file_object?.size || item.file_object?.size
        },
        exported_files: data.exported_files || item.exported_files
      })
    },

    mediaActions(media) {
      return [
        { label: 'Edit', show: this.$checkPermission('medias.update'), action: () => this.editMedia(media), icon: icons.edit },
        { label: 'Preview', show: media.file_object?.status === 'success', action: () => this.showPreview(media), icon: icons.preview },
        { divider: true },
        { label: 'Delete', show: this.$checkPermission('medias.delete'), danger: true, action: () => this.confirmDelete(media), icon: icons.delete }
      ]
    },

    confirmDelete(media) {
      this.mediaToDelete = media
      this.showDeleteModal = true
    },

    async deleteMedia() {
      if (!this.mediaToDelete) return
      this.deleting = true
      try {
        await api.delete(`/medias/${this.mediaToDelete._id}`)
        notifySuccess('Media deleted')
        this.showDeleteModal = false
        this.mediaToDelete = null
        this.fetchMedias()
      } catch { /* toast */ } finally { this.deleting = false }
    },


    getTagsList(tags) {
      if (!tags?.length) return []
      return tags.map(t => {
        if (typeof t === 'object' && t.name) return t
        const id = typeof t === 'object' ? t._id?.toString() : t?.toString()
        const name = this.$refs.tagLookup?.getTagName(id) || id
        return { _id: id, name }
      })
    },

    getPublishableFormats(media) {
      if (!media.exported_files) return []
      return Object.entries(media.exported_files)
        .filter(([id]) => id !== 'original_abr' && id !== 'mp3')
        .map(([id, file]) => ({ id, remoteUrl: file.remoteUrl || file.remotePath, fileSize: file.fileSize, status: file.status || (file.remoteUrl || file.remotePath ? 'success' : 'pending') }))
    },

    getCategoryName(id) {
      if (!id) return ''
      return this.$refs.categoryLookup?.getCategoryName(id) || ''
    },

    openQuickTags(media) {
      this.quickEditMedia = media
      this.quickTagIds = (media.tags || []).map(t => typeof t === 'string' ? t : t._id)
      this.showQuickTags = true
    },

    async saveQuickTags() {
      if (!this.quickEditMedia) return
      this.quickSaving = true
      try {
        await api.patch(`/medias/${this.quickEditMedia._id}`, { tags: this.quickTagIds })
        notifySuccess('Tags updated')
        this.showQuickTags = false
        this.fetchMedias()
      } catch { /* toast */ } finally { this.quickSaving = false }
    },

    openQuickCategory(media) {
      this.quickEditMedia = media
      this.quickCategoryIds = (media.categories || []).map(c => typeof c === 'string' ? c : c._id?.toString())
      this.showQuickCategory = true
    },

    async saveQuickCategory() {
      if (!this.quickEditMedia) return
      this.quickSaving = true
      try {
        await api.patch(`/medias/${this.quickEditMedia._id}`, { categories: this.quickCategoryIds })
        notifySuccess('Categories updated')
        this.showQuickCategory = false
        this.fetchMedias()
      } catch { /* toast */ } finally { this.quickSaving = false }
    },

    clearFilters() {
      this.filters = { publish_status: '', file_status: '', tags: [], category_id: null }
    },

    toggleTags(id) {
      this.expandedTagsId = this.expandedTagsId === id ? null : id
    },

    isProcessing(media) {
      return ['transcoding', 'uploading', 'recording', 'processing'].includes(media.file_object?.status)
    },

    fileStatusClass(media) {
      const s = media.file_object?.status
      if (s === 'success') return 'badge-live'
      if (s === 'failed') return 'badge-error'
      if (['transcoding', 'uploading', 'processing', 'recording'].includes(s)) return 'badge-warning'
      return 'badge-idle'
    },

    editMedia(media) {
      this.$router.push(`/medias/${media._id}/edit`)
    },

    showPreview(media) {
      this.previewMedia = media
      this.showPlayer = true
    },

    async fetchMedias(count = true) {
      try {
        const params = {
          limit: this.pagination.limit,
          page: this.pagination.page
        }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (this.filters.publish_status) params.status = this.filters.publish_status
        if (this.filters.file_status) params.file_status = this.filters.file_status
        if (this.filters.tags?.length) params.tags = this.filters.tags.join(',')
        if (this.filters.category_id) params.categories = this.filters.category_id
        if (count) params.count = true

        const res = await api.get('/medias', { params })
        this.medias = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
      } catch {
        // keep existing data
      } finally {
        this.loading = false
      }
    },

    goToPage(page) {
      this.pagination.page = page
      this.fetchMedias(false)
    },

    mediaType(media) {
      const type = (media.type || media.mimeType || media.content_type || '').toLowerCase()
      if (type.includes('video')) return 'video'
      if (type.includes('audio')) return 'audio'
      return 'video'
    },

    mediaIcon(media) {
      const type = this.mediaType(media)
      if (type === 'audio') {
        return '<svg class="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>'
      }
      return '<svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>'
    },

    mediaIconBg(media) {
      return this.mediaType(media) === 'audio' ? 'bg-violet-50' : 'bg-blue-50'
    },

    formatDuration(ms) {
      if (!ms) return '—'
      const s = Math.floor(ms / 1000)
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = s % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    },

    formatSize(bytes) {
      if (!bytes) return '—'
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let i = 0
      while (size >= 1024 && i < units.length - 1) { size /= 1024; i++ }
      return size.toFixed(i > 0 ? 1 : 0) + ' ' + units[i]
    },


  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
